/**
 * Billing Utilities - Single Point of Truth
 * 
 * This module provides centralized utilities for billing-related formatting,
 * colors, calculations, and status handling across the entire application.
 * 
 * All billing-related UI logic should use these utilities to ensure consistency.
 * 
 * @author Development Team
 * @version 1.0.0
 * @since January 2025
 */

// ============================================================================
// BILLING STATUS CONSTANTS AND UTILITIES
// ============================================================================

export interface BillingStatusInfo {
  status: string
  label: string
  color: string
  bgColor: string
  borderColor: string
  icon: string
  severity: 'success' | 'warning' | 'error' | 'info'
}

export const BILLING_STATUS_CONFIG: Record<string, BillingStatusInfo> = {
  paid: {
    status: 'paid',
    label: 'Paid',
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.2)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    icon: 'CheckCircle',
    severity: 'success'
  },
  active: {
    status: 'active',
    label: 'Active',
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.2)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    icon: 'CheckCircle',
    severity: 'success'
  },
  pending: {
    status: 'pending',
    label: 'Pending',
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.2)',
    borderColor: 'rgba(255, 152, 0, 0.3)',
    icon: 'Schedule',
    severity: 'warning'
  },
  overdue: {
    status: 'overdue',
    label: 'Overdue',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Warning',
    severity: 'error'
  },
  unpaid: {
    status: 'unpaid',
    label: 'Unpaid',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Warning',
    severity: 'error'
  },
  canceled: {
    status: 'canceled',
    label: 'Canceled',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Warning',
    severity: 'error'
  },
  cancelled: {
    status: 'cancelled',
    label: 'Cancelled',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Warning',
    severity: 'error'
  },
  failed: {
    status: 'failed',
    label: 'Failed',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Error',
    severity: 'error'
  },
  blocked: {
    status: 'blocked',
    label: 'Blocked',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Error',
    severity: 'error'
  },
  expired: {
    status: 'expired',
    label: 'Expired',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Error',
    severity: 'error'
  },
  trial: {
    status: 'trial',
    label: 'Trial',
    color: '#2196f3',
    bgColor: 'rgba(33, 150, 243, 0.2)',
    borderColor: 'rgba(33, 150, 243, 0.3)',
    icon: 'Info',
    severity: 'info'
  },
  nothing: {
    status: 'nothing',
    label: 'Nothing',
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.2)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    icon: 'CheckCircle',
    severity: 'success'
  },
  inactive: {
    status: 'inactive',
    label: 'Inactive',
    color: '#9e9e9e',
    bgColor: 'rgba(158, 158, 158, 0.2)',
    borderColor: 'rgba(158, 158, 158, 0.3)',
    icon: 'Info',
    severity: 'info'
  },
  // Stripe Subscription Statuses
  trialing: {
    status: 'trialing',
    label: 'Trialing',
    color: '#2196f3',
    bgColor: 'rgba(33, 150, 243, 0.2)',
    borderColor: 'rgba(33, 150, 243, 0.3)',
    icon: 'Info',
    severity: 'info'
  },
  incomplete: {
    status: 'incomplete',
    label: 'Incomplete',
    color: '#ffc107',
    bgColor: 'rgba(255, 193, 7, 0.2)',
    borderColor: 'rgba(255, 193, 7, 0.3)',
    icon: 'Schedule',
    severity: 'warning'
  },
  incomplete_expired: {
    status: 'incomplete_expired',
    label: 'Expired',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Error',
    severity: 'error'
  },
  past_due: {
    status: 'past_due',
    label: 'Past Due',
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.2)',
    borderColor: 'rgba(255, 152, 0, 0.3)',
    icon: 'Warning',
    severity: 'warning'
  },
  paused: {
    status: 'paused',
    label: 'Paused',
    color: '#9e9e9e',
    bgColor: 'rgba(158, 158, 158, 0.2)',
    borderColor: 'rgba(158, 158, 158, 0.3)',
    icon: 'Pause',
    severity: 'info'
  }
}

// ============================================================================
// BILLING TIER CONSTANTS AND UTILITIES
// ============================================================================

export interface BillingTierInfo {
  tier_key: string
  name: string
  color: string
  bgColor: string
  borderColor: string
  icon: string
  priority: number
}

export const BILLING_TIER_CONFIG: Record<string, BillingTierInfo> = {
  free: {
    tier_key: 'free',
    name: 'Free',
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.2)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    icon: 'Free',
    priority: 1
  },
  starter: {
    tier_key: 'starter',
    name: 'Starter',
    color: '#2196f3',
    bgColor: 'rgba(33, 150, 243, 0.2)',
    borderColor: 'rgba(33, 150, 243, 0.3)',
    icon: 'Star',
    priority: 2
  },
  basic: {
    tier_key: 'basic',
    name: 'Basic',
    color: '#2196f3',
    bgColor: 'rgba(33, 150, 243, 0.2)',
    borderColor: 'rgba(33, 150, 243, 0.3)',
    icon: 'Star',
    priority: 2
  },
  professional: {
    tier_key: 'professional',
    name: 'Professional',
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.2)',
    borderColor: 'rgba(255, 152, 0, 0.3)',
    icon: 'Business',
    priority: 3
  },
  enterprise: {
    tier_key: 'enterprise',
    name: 'Enterprise',
    color: '#9c27b0',
    bgColor: 'rgba(156, 39, 176, 0.2)',
    borderColor: 'rgba(156, 39, 176, 0.3)',
    icon: 'BusinessCenter',
    priority: 4
  },
  unlimited: {
    tier_key: 'unlimited',
    name: 'Unlimited',
    color: '#42eacb',
    bgColor: 'rgba(66, 234, 203, 0.2)',
    borderColor: 'rgba(66, 234, 203, 0.3)',
    icon: 'AllInclusive',
    priority: 5
  },
  custom: {
    tier_key: 'custom',
    name: 'Custom',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Settings',
    priority: 6
  }
}

// ============================================================================
// PAYMENT METHOD STATUS CONSTANTS
// ============================================================================

export interface PaymentMethodStatusInfo {
  status: string
  label: string
  color: string
  bgColor: string
  borderColor: string
  icon: string
  severity: 'success' | 'warning' | 'error' | 'info'
}

export const PAYMENT_METHOD_STATUS_CONFIG: Record<string, PaymentMethodStatusInfo> = {
  active: {
    status: 'active',
    label: 'Active',
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.2)',
    borderColor: 'rgba(76, 175, 80, 0.3)',
    icon: 'CheckCircle',
    severity: 'success'
  },
  pending: {
    status: 'pending',
    label: 'Pending',
    color: '#ff9800',
    bgColor: 'rgba(255, 152, 0, 0.2)',
    borderColor: 'rgba(255, 152, 0, 0.3)',
    icon: 'Schedule',
    severity: 'warning'
  },
  blocked: {
    status: 'blocked',
    label: 'Blocked',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Error',
    severity: 'error'
  },
  expired: {
    status: 'expired',
    label: 'Expired',
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.2)',
    borderColor: 'rgba(244, 67, 54, 0.3)',
    icon: 'Error',
    severity: 'error'
  },
  inactive: {
    status: 'inactive',
    label: 'Inactive',
    color: '#9e9e9e',
    bgColor: 'rgba(158, 158, 158, 0.2)',
    borderColor: 'rgba(158, 158, 158, 0.3)',
    icon: 'Info',
    severity: 'info'
  }
}

// ============================================================================
// BILLING CALCULATION CONSTANTS
// ============================================================================

export const BILLING_CONSTANTS = {
  YEARLY_DISCOUNT_PERCENTAGE: 0.1, // 10% discount for yearly billing
  DEFAULT_CURRENCY: 'USD',
  DEFAULT_BILLING_CYCLE: 'monthly' as const,
  MAX_USERS_UNLIMITED: -1,
  DEFAULT_FREE_USERS_LIMIT: 0,
  DEFAULT_BASE_PRICE: 0,
  DEFAULT_PRICE_PER_USER: 0
} as const

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get billing status information
 */
export function getBillingStatusInfo(status: string): BillingStatusInfo {
  const normalizedStatus = status?.toLowerCase() || 'inactive'
  return BILLING_STATUS_CONFIG[normalizedStatus] || BILLING_STATUS_CONFIG.inactive
}

/**
 * Get billing status color
 */
export function getBillingStatusColor(status: string): string {
  return getBillingStatusInfo(status).color
}

/**
 * Get billing status label
 */
export function getBillingStatusLabel(status: string): string {
  return getBillingStatusInfo(status).label
}

/**
 * Get billing tier information
 */
export function getBillingTierInfo(tier: string | { color?: string; tier_key?: string }): BillingTierInfo {
  // If tier is an object (new BillingTier from database), use its color
  if (typeof tier === 'object' && tier !== null) {
    if (tier.color) {
      return {
        ...BILLING_TIER_CONFIG[tier.tier_key || 'custom'],
        color: tier.color
      }
    }
    // Fall back to tier_key if no color
    if (tier.tier_key) {
      return BILLING_TIER_CONFIG[tier.tier_key] || BILLING_TIER_CONFIG.custom
    }
    return BILLING_TIER_CONFIG.custom
  }
  
  // Handle string tier (old system or tier_key)
  const tierKey = typeof tier === 'string' ? tier.toLowerCase() : 'custom'
  return BILLING_TIER_CONFIG[tierKey] || BILLING_TIER_CONFIG.custom
}

/**
 * Get billing tier color
 */
export function getBillingTierColor(tier: string | { color?: string; tier_key?: string }): string {
  return getBillingTierInfo(tier).color
}

/**
 * Get payment method status information
 */
export function getPaymentMethodStatusInfo(status: string): PaymentMethodStatusInfo {
  const normalizedStatus = status?.toLowerCase() || 'inactive'
  return PAYMENT_METHOD_STATUS_CONFIG[normalizedStatus] || PAYMENT_METHOD_STATUS_CONFIG.inactive
}

/**
 * Get payment method status color
 */
export function getPaymentMethodStatusColor(status: string): string {
  return getPaymentMethodStatusInfo(status).color
}

/**
 * Get payment method status label
 */
export function getPaymentMethodStatusLabel(status: string): string {
  return getPaymentMethodStatusInfo(status).label
}

/**
 * Get subscription status color
 */
export function getSubscriptionStatusColor(status: string): string {
  const normalizedStatus = status?.toLowerCase() || 'inactive'
  const statusInfo = BILLING_STATUS_CONFIG[normalizedStatus] || BILLING_STATUS_CONFIG.inactive
  return statusInfo.color
}

/**
 * Get subscription status label
 */
export function getSubscriptionStatusLabel(status: string): string {
  const normalizedStatus = status?.toLowerCase() || 'inactive'
  const statusInfo = BILLING_STATUS_CONFIG[normalizedStatus] || BILLING_STATUS_CONFIG.inactive
  return statusInfo.label
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number, currency: string = BILLING_CONSTANTS.DEFAULT_CURRENCY): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount)
}

/**
 * Authoritative member count for billing UIs: max(Parse roster total, billing active_users) when the latter is a finite number.
 * Matches CompanyManagerView / CompanyUserView semantics for aligning previews with Billing Status.
 */
export function mergeMemberCountForBilling(
  parseTotal: number | null,
  billingActiveUsers: number | null | undefined
): number {
  const parse = parseTotal ?? 0
  if (
    billingActiveUsers != null &&
    typeof billingActiveUsers === 'number' &&
    Number.isFinite(billingActiveUsers)
  ) {
    return Math.max(parse, billingActiveUsers)
  }
  return parse
}

/**
 * Calculate billing cost for a tier
 */
export function calculateBillingCost(
  tier: {
    base_price: number
    price_per_user: number
    free_users_limit: number
  },
  userCount: number,
  billingCycle: 'monthly' | 'yearly' = BILLING_CONSTANTS.DEFAULT_BILLING_CYCLE
): {
  basePrice: number
  pricePerUser: number
  freeUsersLimit: number
  userCount: number
  billableUsers: number
  totalBaseCost: number
  totalUserCost: number
  totalCost: number
  billingCycle: string
  breakdown: {
    freeUsers: number
    billableUsers: number
    baseCost: number
    userCost: number
    totalCost: number
  }
} {
  const basePrice = tier.base_price || BILLING_CONSTANTS.DEFAULT_BASE_PRICE
  const pricePerUser = tier.price_per_user || BILLING_CONSTANTS.DEFAULT_PRICE_PER_USER
  const freeUsersLimit = tier.free_users_limit || BILLING_CONSTANTS.DEFAULT_FREE_USERS_LIMIT
  
  // Calculate billable users (users beyond the free limit)
  const billableUsers = Math.max(0, userCount - freeUsersLimit)
  
  // Calculate base cost
  let totalBaseCost = basePrice
  
  // Calculate per-user cost
  let totalUserCost = billableUsers * pricePerUser
  
  // Apply yearly discount (10% off)
  if (billingCycle === 'yearly') {
    totalBaseCost = totalBaseCost * 12 * (1 - BILLING_CONSTANTS.YEARLY_DISCOUNT_PERCENTAGE)
    totalUserCost = totalUserCost * 12 * (1 - BILLING_CONSTANTS.YEARLY_DISCOUNT_PERCENTAGE)
  }
  
  const totalCost = totalBaseCost + totalUserCost
  
  return {
    basePrice,
    pricePerUser,
    freeUsersLimit,
    userCount,
    billableUsers,
    totalBaseCost,
    totalUserCost,
    totalCost,
    billingCycle,
    breakdown: {
      freeUsers: Math.min(userCount, freeUsersLimit),
      billableUsers: billableUsers,
      baseCost: totalBaseCost,
      userCost: totalUserCost,
      totalCost: totalCost
    }
  }
}

/**
 * Check if a billing tier is completely free
 */
export function isFreeTier(tier: {
  base_price: number
  price_per_user: number
}): boolean {
  return tier.base_price === 0 && tier.price_per_user === 0
}

/**
 * Check if a billing calculation represents a free tier
 */
export function isFreeTierCalculation(calculation: {
  base_price: number
  price_per_user: number
}): boolean {
  return calculation.base_price === 0 && calculation.price_per_user === 0
}

/**
 * Normalize billing tier name
 */
export function normalizeBillingTier(tier: string): string {
  const tierMap: Record<string, string> = {
    'basic': 'starter',
    'pro': 'professional',
    'ent': 'enterprise',
    'unlim': 'unlimited'
  }
  
  return tierMap[tier?.toLowerCase()] || tier?.toLowerCase() || 'custom'
}

/**
 * Get tier priority for sorting
 */
export function getTierPriority(tierKey: string): number {
  const tierInfo = getBillingTierInfo(tierKey)
  return tierInfo.priority
}

/**
 * Sort tiers by priority
 */
export function sortTiersByPriority<T extends { tier_key: string }>(tiers: T[]): T[] {
  return [...tiers].sort((a, b) => {
    const priorityA = getTierPriority(a.tier_key)
    const priorityB = getTierPriority(b.tier_key)
    return priorityA - priorityB
  })
}

/**
 * Get usage percentage for a tier
 */
export function getUsagePercentage(currentUsers: number, maxUsers: number): number {
  if (maxUsers === BILLING_CONSTANTS.MAX_USERS_UNLIMITED) {
    return 0 // Unlimited users
  }
  
  if (maxUsers === 0) {
    return 100 // No users allowed
  }
  
  return Math.min(100, (currentUsers / maxUsers) * 100)
}

/**
 * Get usage color based on percentage
 */
export function getUsageColor(percentage: number): string {
  if (percentage >= 100) return '#f44336' // Red - at or over limit
  if (percentage >= 90) return '#ff9800'  // Orange - approaching limit
  if (percentage >= 80) return '#ffc107'  // Yellow - high usage
  return '#4caf50' // Green - normal usage
}

/**
 * Get usage status based on percentage
 */
export function getUsageStatus(percentage: number): 'normal' | 'high' | 'critical' | 'over_limit' {
  if (percentage >= 100) return 'over_limit'
  if (percentage >= 90) return 'critical'
  if (percentage >= 80) return 'high'
  return 'normal'
}

/**
 * Format date for billing display
 */
export function formatBillingDate(dateValue: any): string {
  if (!dateValue) return 'N/A'
  
  try {
    const date = new Date(dateValue)
    if (isNaN(date.getTime())) return 'Invalid Date'
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting billing date:', error)
    return 'Invalid Date'
  }
}

/**
 * Format billing date with relative time (e.g., "Dec 15, 2024 (in 5 days)")
 */
export function formatBillingDateWithRelative(dateValue: any): string {
  if (!dateValue) return 'N/A'
  
  try {
    const date = new Date(dateValue)
    if (isNaN(date.getTime())) return 'Invalid Date'
    
    const now = new Date()
    const diffTime = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    
    if (diffDays < 0) {
      return `${formattedDate} (${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago)`
    } else if (diffDays === 0) {
      return `${formattedDate} (today)`
    } else if (diffDays === 1) {
      return `${formattedDate} (tomorrow)`
    } else if (diffDays <= 7) {
      return `${formattedDate} (in ${diffDays} days)`
    } else {
      return `${formattedDate} (in ${diffDays} days)`
    }
  } catch (error) {
    console.error('Error formatting billing date with relative:', error)
    return 'Invalid Date'
  }
}

/**
 * Format subscription period dates for clear display
 */
export function formatSubscriptionPeriod(startDate: any, endDate: any): {
  start: string
  end: string
  period: string
  relativeEnd: string
} {
  try {
    // Handle Unix timestamps (convert to milliseconds)
    const start = new Date(typeof startDate === 'number' ? startDate * 1000 : startDate)
    const end = new Date(typeof endDate === 'number' ? endDate * 1000 : endDate)
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return {
        start: 'N/A',
        end: 'N/A',
        period: 'N/A',
        relativeEnd: 'N/A'
      }
    }
    
    const startFormatted = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    
    const endFormatted = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    
    const now = new Date()
    const diffTime = end.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    let relativeEnd = ''
    if (diffDays < 0) {
      relativeEnd = `(${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago)`
    } else if (diffDays === 0) {
      relativeEnd = '(today)'
    } else if (diffDays === 1) {
      relativeEnd = '(tomorrow)'
    } else {
      relativeEnd = `(in ${diffDays} days)`
    }
    
    return {
      start: startFormatted,
      end: endFormatted,
      period: `${startFormatted} - ${endFormatted}`,
      relativeEnd: `${endFormatted} ${relativeEnd}`
    }
  } catch (error) {
    console.error('Error formatting subscription period:', error)
    return {
      start: 'N/A',
      end: 'N/A',
      period: 'N/A',
      relativeEnd: 'N/A'
    }
  }
}

/**
 * Get billing cycle information with clear formatting
 */
export function getBillingCycleInfo(subscription: any): {
  cycle: string
  nextBilling: string
  nextBillingRelative: string
  isAutoRenew: boolean
  status: string
} {
  try {
    const cycle = subscription?.items?.data?.[0]?.price?.recurring?.interval || 'month'
    const isAutoRenew = !subscription?.cancel_at_period_end
    const status = subscription?.status || 'unknown'
    
    // Handle both old and new Stripe API formats for period dates
    let nextBillingDate = null
    
    // New API format (2025-07-30+): periods are in subscription items
    if (subscription?.items?.data?.[0]?.current_period_end) {
      nextBillingDate = subscription.items.data[0].current_period_end
    }
    // Old API format: periods are on subscription object
    else if (subscription?.current_period_end) {
      nextBillingDate = subscription.current_period_end
    }
    // Fallback to billing_cycle_anchor
    else if (subscription?.billing_cycle_anchor) {
      nextBillingDate = subscription.billing_cycle_anchor
    }
    
    let nextBilling = 'N/A'
    let nextBillingRelative = 'N/A'
    
    if (nextBillingDate) {
      const date = new Date(nextBillingDate * 1000)
      nextBilling = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      
      const now = new Date()
      const diffTime = date.getTime() - now.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      
      if (diffDays < 0) {
        nextBillingRelative = `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`
      } else if (diffDays === 0) {
        nextBillingRelative = 'today'
      } else if (diffDays === 1) {
        nextBillingRelative = 'tomorrow'
      } else {
        nextBillingRelative = `in ${diffDays} days`
      }
    }
    
    return {
      cycle: cycle.charAt(0).toUpperCase() + cycle.slice(1),
      nextBilling,
      nextBillingRelative,
      isAutoRenew,
      status
    }
  } catch (error) {
    console.error('Error getting billing cycle info:', error)
    return {
      cycle: 'Monthly',
      nextBilling: 'N/A',
      nextBillingRelative: 'N/A',
      isAutoRenew: false,
      status: 'unknown'
    }
  }
}

/**
 * Format time remaining for billing periods
 */
export function formatTimeRemaining(hours: number): string {
  if (hours <= 0) return 'Expired'
  
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''} ${remainingHours > 0 ? `${remainingHours}h` : ''}`.trim()
  }
  
  return `${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`
}

/**
 * Get billing cycle display name
 */
export function getBillingCycleDisplayName(cycle: string): string {
  switch (cycle?.toLowerCase()) {
    case 'monthly':
      return 'Monthly'
    case 'yearly':
    case 'annual':
      return 'Yearly'
    default:
      return 'Monthly'
  }
}

/**
 * Calculate savings for yearly billing
 */
export function calculateYearlySavings(monthlyCost: number): number {
  const yearlyCost = monthlyCost * 12
  const discountedYearlyCost = yearlyCost * (1 - BILLING_CONSTANTS.YEARLY_DISCOUNT_PERCENTAGE)
  return yearlyCost - discountedYearlyCost
}

/**
 * Get billing action message
 */
export function getBillingActionMessage(action: string): string {
  switch (action?.toLowerCase()) {
    case 'addition':
      return 'Adding user will result in additional charges'
    case 'removal':
      return 'Removing user will result in prorated credits'
    case 'upgrade':
      return 'Upgrading tier will result in additional charges'
    case 'downgrade':
      return 'Downgrading tier will result in prorated credits'
    default:
      return 'This action will affect your billing'
  }
}

/**
 * Get billing action severity
 */
export function getBillingActionSeverity(action: string): 'success' | 'warning' | 'error' | 'info' {
  switch (action?.toLowerCase()) {
    case 'addition':
    case 'upgrade':
      return 'warning'
    case 'removal':
    case 'downgrade':
      return 'info'
    default:
      return 'info'
  }
}

/**
 * Check if a subscription is active and valid for billing purposes
 * Based on Stripe documentation: https://docs.stripe.com/billing/subscriptions/overview#subscription-statuses
 */
export function isActiveSubscription(subscription: any): boolean {
  if (!subscription || !subscription.status) {
    return false
  }

  const status = subscription.status.toLowerCase()
  
  // Active statuses that should be considered "active" for billing purposes
  const activeStatuses = ['active', 'trialing', 'past_due']
  
  return activeStatuses.includes(status)
}

/**
 * Check if a subscription is in a billing-valid state
 * This includes subscriptions that are active, trialing, or past_due but still generating invoices
 */
export function isBillingValidSubscription(subscription: any): boolean {
  if (!subscription || !subscription.status) {
    return false
  }

  const status = subscription.status.toLowerCase()
  
  // Statuses that are valid for billing (generate invoices)
  const billingValidStatuses = ['active', 'trialing', 'past_due', 'incomplete']
  
  return billingValidStatuses.includes(status)
}

/**
 * Check if a subscription is canceled or expired
 */
export function isCanceledSubscription(subscription: any): boolean {
  if (!subscription || !subscription.status) {
    return false
  }

  const status = subscription.status.toLowerCase()
  
  // Terminal statuses that indicate cancellation
  const canceledStatuses = ['canceled', 'cancelled', 'incomplete_expired', 'unpaid', 'paused']
  
  return canceledStatuses.includes(status)
}

/**
 * Get subscription status information with detailed categorization
 */
export function getSubscriptionStatusInfo(subscription: any): {
  isActive: boolean
  isBillingValid: boolean
  isCanceled: boolean
  status: string
  statusCategory: 'active' | 'trial' | 'past_due' | 'incomplete' | 'canceled' | 'unknown'
  description: string
} {
  if (!subscription || !subscription.status) {
    return {
      isActive: false,
      isBillingValid: false,
      isCanceled: true,
      status: 'unknown',
      statusCategory: 'unknown',
      description: 'No subscription data available'
    }
  }

  const status = subscription.status.toLowerCase()
  const isActive = isActiveSubscription(subscription)
  const isBillingValid = isBillingValidSubscription(subscription)
  const isCanceled = isCanceledSubscription(subscription)

  let statusCategory: 'active' | 'trial' | 'past_due' | 'incomplete' | 'canceled' | 'unknown' = 'unknown'
  let description = ''

  switch (status) {
    case 'active':
      statusCategory = 'active'
      description = 'Subscription is in good standing and billing normally'
      break
    case 'trialing':
      statusCategory = 'trial'
      description = 'Subscription is in trial period, will become active after first payment'
      break
    case 'past_due':
      statusCategory = 'past_due'
      description = 'Payment failed, subscription continues to generate invoices'
      break
    case 'incomplete':
      statusCategory = 'incomplete'
      description = 'Customer must complete payment within 23 hours to activate'
      break
    case 'incomplete_expired':
      statusCategory = 'canceled'
      description = 'Initial payment failed and subscription expired'
      break
    case 'canceled':
    case 'cancelled':
      statusCategory = 'canceled'
      description = 'Subscription was canceled and is no longer active'
      break
    case 'unpaid':
      statusCategory = 'canceled'
      description = 'Subscription is unpaid and access should be revoked'
      break
    case 'paused':
      statusCategory = 'canceled'
      description = 'Subscription is paused due to missing payment method'
      break
    default:
      statusCategory = 'unknown'
      description = `Unknown subscription status: ${status}`
  }

  return {
    isActive,
    isBillingValid,
    isCanceled,
    status,
    statusCategory,
    description
  }
}

/** Stripe.js config from billing-comprehensive or GET /api/stripe/config (`data` / `stripe_config` shape). */
export function pickStripePublishableConfig(
  raw: unknown
): (Record<string, unknown> & { publishableKey: string }) | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const direct = o.direct as Record<string, unknown> | undefined
  const cloudWrap = o.cloud as { config?: Record<string, unknown> } | undefined
  const cloud = cloudWrap?.config
  const merged = direct || cloud || (typeof o.publishableKey === 'string' ? o : null)
  if (merged && typeof merged.publishableKey === 'string' && merged.publishableKey.length > 0) {
    return merged as Record<string, unknown> & { publishableKey: string }
  }
  return null
}

// ============================================================================
// EXPORT ALL UTILITIES
// ============================================================================

export default {
  // Status utilities
  getBillingStatusInfo,
  getBillingStatusColor,
  getBillingStatusLabel,
  
  // Tier utilities
  getBillingTierInfo,
  getBillingTierColor,
  
  // Payment method utilities
  getPaymentMethodStatusInfo,
  getPaymentMethodStatusColor,
  getPaymentMethodStatusLabel,
  
  // Calculation utilities
  formatCurrency,
  calculateBillingCost,
  isFreeTier,
  isFreeTierCalculation,
  normalizeBillingTier,
  getTierPriority,
  sortTiersByPriority,
  getUsagePercentage,
  getUsageColor,
  getUsageStatus,
  
  // Formatting utilities
  formatBillingDate,
  formatBillingDateWithRelative,
  formatSubscriptionPeriod,
  getBillingCycleInfo,
  formatTimeRemaining,
  getBillingCycleDisplayName,
  calculateYearlySavings,
  getBillingActionMessage,
  getBillingActionSeverity,
  
  // Subscription status utilities
  isActiveSubscription,
  isBillingValidSubscription,
  isCanceledSubscription,
  getSubscriptionStatusInfo,
  
  // Constants
  BILLING_STATUS_CONFIG,
  BILLING_TIER_CONFIG,
  PAYMENT_METHOD_STATUS_CONFIG,
  BILLING_CONSTANTS,

  pickStripePublishableConfig,
}
